import { useEffect, useRef, useState, useCallback, useContext } from 'react';
import { AuthContext } from '../context/authContext';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Fill, Stroke, Text } from 'ol/style';

const partidoStyle = new Style({
  fill: new Fill({ color: 'rgba(255, 0, 0, 0.15)' }),
  stroke: new Stroke({ color: 'red', width: 2 }),
});

const renabapTargetStyle = (feature) => {
  const nombre = feature.get('nombre') || '';
  return new Style({
    fill: new Fill({ color: 'rgba(255, 193, 7, 0.25)' }),
    stroke: new Stroke({ color: '#F9A825', width: 4 }),
    text: nombre ? new Text({
      text: nombre,
      fill: new Fill({ color: '#F57F17' }),
      stroke: new Stroke({ color: '#fff', width: 3 }),
      font: 'bold 13px sans-serif',
      offsetY: -10,
    }) : undefined,
  });
};

const rppvapTargetStyle = (feature) => {
  const nombre = feature.get('nombre') || '';
  return new Style({
    fill: new Fill({ color: 'rgba(244, 67, 54, 0.25)' }),
    stroke: new Stroke({ color: '#E53935', width: 4 }),
    text: nombre ? new Text({
      text: nombre,
      fill: new Fill({ color: '#C62828' }),
      stroke: new Stroke({ color: '#fff', width: 3 }),
      font: 'bold 13px sans-serif',
      offsetY: -10,
    }) : undefined,
  });
};

const renabapNonTargetStyle = new Style({
  fill: new Fill({ color: 'rgba(255, 193, 7, 0.25)' }),
  stroke: new Stroke({ color: '#F9A825', width: 2, lineDash: [6, 4] }),
});

const rppvapNonTargetStyle = new Style({
  fill: new Fill({ color: 'rgba(244, 67, 54, 0.25)' }),
  stroke: new Stroke({ color: '#E53935', width: 2, lineDash: [6, 4] }),
});

const noTargetStyle = new Style({
  fill: new Fill({ color: 'rgba(128, 128, 128, 0.06)' }),
  stroke: new Stroke({ color: 'rgba(128, 128, 128, 0.25)', width: 1, lineDash: [4, 4] }),
});

export default function useBarrioLayers({ map, partido, renabapIds, rppvapCodigos, noAutoZoomRef }) {
  const { token } = useContext(AuthContext);
  const [layerVisibility, setLayerVisibility] = useState({
    partido: true,
    renabap: true,
    rppvap: true,
    barriosNoTarget: true,
    renabapTarget: true,
    rppvapTarget: true,
  });
  const layersRef = useRef({});
  const targetFeaturesRef = useRef([]);
  const allBarriosFeaturesRef = useRef([]);
  const [barrioCounts, setBarrioCounts] = useState({ targets: 0, total: 0 });

  const [layersLoaded, setLayersLoaded] = useState(false);

  const hasRenabapTargets = useRef(false);
  const hasRppvapTargets = useRef(false);
  const [hasRenabapData, setHasRenabapData] = useState(false);
  const [hasRppvapData, setHasRppvapData] = useState(false);

  const targetIdsRef = useRef(new Set());

  // Re-clasifica los polígonos ya cargados cuando cambian los targets (sin re-fetch).
  // Evita que la capa de barrios se apague momentáneamente al confirmar un punto
  // en un polígono no-target (el efecto principal ya no depende de renabapIds/rppvapCodigos).
  const reclassifyTargets = useCallback(() => {
    const sources = {
      renabap: layersRef.current.renabap?.getSource(),
      rppvap: layersRef.current.rppvap?.getSource(),
      barriosNoTarget: layersRef.current.barriosNoTarget?.getSource(),
      renabapTarget: layersRef.current.renabapTarget?.getSource(),
      rppvapTarget: layersRef.current.rppvapTarget?.getSource(),
    };
    const existing = Object.values(sources).filter(Boolean);
    if (existing.length === 0) return;

    const targetIds = targetIdsRef.current;
    const targetFeatures = [];
    existing.forEach(s => s.clear());
    for (const feature of allBarriosFeaturesRef.current) {
      const props = feature.getProperties();
      const tipo = props.tipo;
      const fid = tipo === 'RENABAP' ? `renabap_${props.id}` : `rppvap_${props.id}`;
      const isTarget = targetIds.has(fid);
      if (isTarget) targetFeatures.push(feature);
      const destKey = tipo === 'RENABAP' ? (isTarget ? 'renabapTarget' : 'renabap')
        : tipo === 'RPPVAP' ? (isTarget ? 'rppvapTarget' : 'rppvap')
        : 'barriosNoTarget';
      const dest = sources[destKey];
      if (dest) dest.addFeature(feature);
    }
    targetFeaturesRef.current = targetFeatures.filter(f => f.getGeometry());
    hasRenabapTargets.current = targetFeatures.some(f => f.getProperties().tipo === 'RENABAP');
    hasRppvapTargets.current = targetFeatures.some(f => f.getProperties().tipo === 'RPPVAP');
    setBarrioCounts(prev => ({ ...prev, targets: targetFeatures.length }));
  }, []);

  useEffect(() => {
    const targetIds = new Set();
    if (renabapIds) renabapIds.split(',').forEach(id => targetIds.add(`renabap_${id.trim()}`));
    if (rppvapCodigos) rppvapCodigos.split(',').forEach(c => targetIds.add(`rppvap_${c.trim()}`));
    targetIdsRef.current = targetIds;
    reclassifyTargets();
  }, [renabapIds, rppvapCodigos, reclassifyTargets]);

  useEffect(() => {
    if (!map || !partido || partido === 0) return;

    const targetIds = targetIdsRef.current;

    const loadedLayers = [];

    const loadLayers = async () => {
      let partidoExtent = null;

      try {
        const res = await fetch(`/api/partido_geom/${partido}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.type === 'FeatureCollection' && data.features.length > 0) {
            const source = new VectorSource({
              features: new GeoJSON().readFeatures(data, {
                featureProjection: 'EPSG:4326',
                dataProjection: 'EPSG:4326',
              }),
            });
            const layer = new VectorLayer({ source, style: partidoStyle, zIndex: 1 });
            map.addLayer(layer);
            loadedLayers.push(layer);
            layersRef.current.partido = layer;
            partidoExtent = source.getExtent();
          }
        }
      } catch (e) {
        console.warn('Error fetching partido geometry:', e);
      }

      try {
        const res = await fetch(`/api/barrios/todos_geom?partido=${partido}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.type === 'FeatureCollection' && data.features.length > 0) {
            const allFeatures = new GeoJSON().readFeatures(data, {
              featureProjection: 'EPSG:4326',
              dataProjection: 'EPSG:4326',
            });

            const renabapNonTarget = [];
            const rppvapNonTarget = [];
            const renabapTarget = [];
            const rppvapTarget = [];
            const otherTypes = [];
            const targetFeatures = [];

            allFeatures.forEach(feature => {
              const props = feature.getProperties();
              const tipo = props.tipo;
              const fid = tipo === 'RENABAP' ? `renabap_${props.id}` : `rppvap_${props.id}`;
              const isTarget = targetIds.has(fid);
              if (isTarget) targetFeatures.push(feature);
              if (tipo === 'RENABAP') {
                if (isTarget) renabapTarget.push(feature);
                else renabapNonTarget.push(feature);
              } else if (tipo === 'RPPVAP') {
                if (isTarget) rppvapTarget.push(feature);
                else rppvapNonTarget.push(feature);
              } else {
                otherTypes.push(feature);
              }
            });

            targetFeaturesRef.current = targetFeatures.filter(f => f.getGeometry());
            allBarriosFeaturesRef.current = allFeatures.filter(f => f.getGeometry());
            setBarrioCounts({
              targets: targetFeatures.length,
              total: allFeatures.length,
              renabap: renabapNonTarget.length + renabapTarget.length,
              rppvap: rppvapNonTarget.length + rppvapTarget.length,
              otros: otherTypes.length,
            });
            hasRenabapTargets.current = renabapTarget.length > 0;
            hasRppvapTargets.current = rppvapTarget.length > 0;
            setHasRenabapData(renabapNonTarget.length > 0 || renabapTarget.length > 0);
            setHasRppvapData(rppvapNonTarget.length > 0 || rppvapTarget.length > 0);

            // Barrios RENABAP (no-target) -- fill tenue + borde punteado
            if (renabapNonTarget.length > 0) {
              const source = new VectorSource({ features: renabapNonTarget });
              const layer = new VectorLayer({ source, style: renabapNonTargetStyle, zIndex: 3.1 });
              map.addLayer(layer);
              loadedLayers.push(layer);
              layersRef.current.renabap = layer;
            }

            // Barrios RPPVAP (no-target) -- fill tenue + borde punteado
            if (rppvapNonTarget.length > 0) {
              const source = new VectorSource({ features: rppvapNonTarget });
              const layer = new VectorLayer({ source, style: rppvapNonTargetStyle, zIndex: 3 });
              map.addLayer(layer);
              loadedLayers.push(layer);
              layersRef.current.rppvap = layer;
            }

            // Otros tipos (ni RENABAP ni RPPVAP)
            if (otherTypes.length > 0) {
              const source = new VectorSource({ features: otherTypes });
              const layer = new VectorLayer({ source, style: noTargetStyle, zIndex: 2 });
              map.addLayer(layer);
              loadedLayers.push(layer);
              layersRef.current.barriosNoTarget = layer;
            }

            // Barrio seleccionado RENABAP -- condicional, con borde
            if (renabapTarget.length > 0) {
              const source = new VectorSource({ features: renabapTarget });
              const layer = new VectorLayer({ source, style: renabapTargetStyle, zIndex: 4.1 });
              map.addLayer(layer);
              loadedLayers.push(layer);
              layersRef.current.renabapTarget = layer;
            }

            // Barrio seleccionado RPPVAP -- condicional, con borde
            if (rppvapTarget.length > 0) {
              const source = new VectorSource({ features: rppvapTarget });
              const layer = new VectorLayer({ source, style: rppvapTargetStyle, zIndex: 4 });
              map.addLayer(layer);
              loadedLayers.push(layer);
              layersRef.current.rppvapTarget = layer;
            }

            if (!noAutoZoomRef?.current) {
              if (targetFeatures.length > 0) {
                const targetSource = new VectorSource({ features: targetFeatures.filter(f => f.getGeometry()) });
                const extent = targetSource.getExtent();
                if (extent) {
                  map.getView().fit(extent, { padding: [100, 100, 100, 100], duration: 1000, maxZoom: 17 });
                }
              } else if (partidoExtent) {
                map.getView().fit(partidoExtent, { padding: [100, 100, 100, 100], duration: 1000, maxZoom: 17 });
              }
            }
            setLayersLoaded(true);
          }
        }
      } catch (e) {
        setLayersLoaded(true);
        console.warn('Error fetching barrio geometries:', e);
      }
    };

    loadLayers();

    return () => {
      loadedLayers.forEach(layer => map.removeLayer(layer));
      layersRef.current = {};
    };
  }, [map, partido, noAutoZoomRef, token]);

  const toggleLayer = useCallback((key) => {
    setLayerVisibility(prev => {
      const next = { ...prev, [key]: !prev[key] };
      layersRef.current[key]?.setVisible(next[key]);
      return next;
    });
  }, []);

  return {
    targetFeaturesRef,
    allBarriosFeaturesRef,
    barrioCounts,
    layerVisibility,
    toggleLayer,
    layersRef,
    hasRenabapTargets,
    hasRppvapTargets,
    hasRenabapData,
    hasRppvapData,
    layersLoaded,
  };
}
